import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../lib/api.js";
import type { AlexaAccountInfo } from "../types/index.js";
import styles from "./AlexaSettings.module.css";

type AlexaState =
  | { status: "idle" }
  | { status: "generating"; code: string; expiresAt: Date }
  | { status: "error"; message: string };

interface Props {
  shareToken: string | null;
  displayName: string;
}

function formatCode(code: string): string {
  if (code.length <= 3) return code;
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
}

export function AlexaSettings({ shareToken, displayName }: Props) {
  const [state, setState] = useState<AlexaState>({ status: "idle" });
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);
  const [accounts, setAccounts] = useState<AlexaAccountInfo[]>([]);
  const [unlinking, setUnlinking] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    if (!shareToken) return;
    try {
      const result = await api.listAlexaAccounts(shareToken);
      setAccounts(result.accounts);
    } catch {
      // Silently fail — accounts list is supplementary info
    }
  }, [shareToken]);

  // Fetch accounts on mount and when shareToken changes
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Countdown timer for code expiry
  useEffect(() => {
    if (state.status !== "generating") return;

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.ceil((state.expiresAt.getTime() - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        setState({ status: "idle" });
        fetchAccounts();
      }
    };

    updateRemaining();
    timerRef.current = setInterval(updateRemaining, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state, fetchAccounts]);

  const handleGenerate = useCallback(async () => {
    if (!shareToken) return;

    setState({ status: "idle" });
    try {
      const result = await api.createAlexaLinkCode(shareToken, displayName);
      setState({
        status: "generating",
        code: result.code,
        expiresAt: new Date(result.expiresAt),
      });
    } catch (err) {
      console.error("Failed to create Alexa link code:", err);
      setState({ status: "error", message: "Alexa連携コードの生成に失敗しました。" });
    }
  }, [shareToken, displayName]);

  const handleUnlink = useCallback(
    async (accountId: string) => {
      if (!shareToken) return;
      setUnlinking(accountId);
      try {
        await api.unlinkAlexaAccount(shareToken, accountId);
        await fetchAccounts();
      } catch (err) {
        console.error("Failed to unlink Alexa account:", err);
      } finally {
        setUnlinking(null);
      }
    },
    [shareToken, fetchAccounts],
  );

  if (state.status === "generating") {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return (
      <section className={styles.container}>
        <p className={styles.label}>Alexa連携コード</p>
        <p className={styles.code}>{formatCode(state.code)}</p>
        <p className={styles.expiry}>
          有効期限: {minutes}:{seconds.toString().padStart(2, "0")}
        </p>
        <p className={styles.hint}>Alexaに「コードは{state.code}」と伝えてください</p>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      {state.status === "error" && (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      )}
      {accounts.length > 0 && (
        <div className={styles.accountList}>
          <p className={styles.label}>連携中のAlexa</p>
          {accounts.map((account) => (
            <div key={account.accountId} className={styles.accountItem}>
              <div className={styles.accountInfo}>
                <span className={styles.accountName}>{account.displayName || "Alexa"}</span>
                <span className={styles.accountDate}>{formatDate(account.linkedAt)}</span>
              </div>
              <button
                type="button"
                className={styles.unlinkButton}
                onClick={() => handleUnlink(account.accountId)}
                disabled={unlinking === account.accountId}
              >
                {unlinking === account.accountId ? "解除中..." : "解除"}
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className={styles.generateButton}
        onClick={handleGenerate}
        disabled={!shareToken}
      >
        Alexa連携コードを生成
      </button>
    </section>
  );
}
